import { Link } from 'react-router-dom';
import type { ProductVariant } from '../data/products';
import { ArrowLeft, BadgeCheck, ShieldCheck, Truck } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import ProductGallery from '../features/product/components/ProductGallery';
import ProductInfo from '../features/product/components/ProductInfo';
import RelatedProducts from '../features/product/components/RelatedProducts';
import { useProductDetail } from '../features/product/useProductDetail';

export default function ProductDetails() {
  const { language } = useTheme();
  const { slug, product, images, loadError, isLoading, setSelectedVariant } = useProductDetail();
  const isPendingProduct = Boolean(slug) && isLoading && !product;

  if (loadError) {
    return (
      <div className="min-h-screen bg-[var(--ubuy-page-bg)]">
        <div className="container mx-auto px-4 py-8 pt-28 text-destructive">
          {loadError}
        </div>
      </div>
    );
  }

  if (isPendingProduct) return <ProductDetailsSkeleton />;

  if (!product) {
    return (
      <div className="min-h-screen bg-[var(--ubuy-page-bg)]">
        <div className="container mx-auto px-4 py-8 pt-28">Not Found</div>
      </div>
    );
  }

  // Handle variant change from ProductInfo
  const handleVariantChange = (variant: ProductVariant) => setSelectedVariant(variant);

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <div className="mx-auto max-w-[1375px] animate-in fade-in px-[25px] pb-16 pt-24 duration-500 sm:pb-20 sm:pt-28">
        <div className="mb-6 flex min-w-0 items-center gap-1.5 text-xs font-bold text-[#747982]">
          <Link to="/" className="flex shrink-0 items-center gap-1 transition hover:text-[#111318]">
            <ArrowLeft size={13} />
            {language === 'mn' ? 'Нүүр' : 'Home'}
          </Link>
          <span className="shrink-0">/</span>
          <Link to="/products" className="shrink-0 transition hover:text-[#111318]">
            {language === 'mn' ? 'Бараа' : 'Products'}
          </Link>
          <span className="shrink-0">/</span>
          <span className="min-w-0 flex-1 truncate text-[#111318]">{product.name}</span>
        </div>

        <div className="mb-6 grid gap-3 rounded-[18px] border border-[#e6e6e6] bg-white p-4 shadow-sm sm:grid-cols-3">
          <div className="flex items-center gap-3 rounded-[14px] bg-[#f7f7f7] p-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#fff3d1] text-[#f6a800]">
              <Truck size={20} />
            </span>
            <div>
              <p className="text-sm font-black text-[#111318]">{language === 'mn' ? 'Монголд авах боломжтой' : 'Available for Mongolia'}</p>
              <p className="text-xs font-semibold text-[#747982]">{language === 'mn' ? 'Cargo workflow хадгалагдана' : 'Pickup workflow supported'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-[14px] bg-[#f7f7f7] p-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e9f9f1] text-[#18a970]">
              <BadgeCheck size={20} />
            </span>
            <div>
              <p className="text-sm font-black text-[#111318]">{language === 'mn' ? 'Сонголт баталгаатай' : 'Selection checked'}</p>
              <p className="text-xs font-semibold text-[#747982]">{language === 'mn' ? 'Variant, size, тоо ширхэг' : 'Variant, size, quantity'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-[14px] bg-[#f7f7f7] p-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#eef6ff] text-[#2f7ed8]">
              <ShieldCheck size={20} />
            </span>
            <div>
              <p className="text-sm font-black text-[#111318]">{language === 'mn' ? 'Secure checkout' : 'Secure checkout'}</p>
              <p className="text-xs font-semibold text-[#747982]">{language === 'mn' ? 'QPay дараагийн алхамд' : 'Payment handled next'}</p>
            </div>
          </div>
        </div>

        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.04fr)_minmax(390px,0.96fr)] lg:gap-8 xl:gap-10 [&>*]:min-w-0">
          <ProductGallery key={`${product.id}-gallery`} images={images} name={product.name} />
          <ProductInfo
            key={`${product.id}-info`}
            product={product}
            onVariantChange={handleVariantChange}
          />
        </div>

        <RelatedProducts currentSlug={product.slug ?? slug ?? undefined} />
      </div>
    </div>
  );
}

function ProductDetailsSkeleton() {
  return (
    <div className="min-h-screen bg-[var(--ubuy-page-bg)]">
      <div className="mx-auto max-w-[1375px] animate-pulse px-[25px] py-8 pt-28">
        <div className="mb-8 h-4 w-40 rounded bg-[var(--ubuy-border-soft)]" />
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="aspect-square rounded-[15px] bg-[var(--ubuy-border-soft)]" />
          <div className="space-y-4">
            <div className="h-6 w-3/4 rounded bg-[var(--ubuy-border-soft)]" />
            <div className="h-4 w-full rounded bg-[var(--ubuy-border-soft)]" />
            <div className="h-4 w-2/3 rounded bg-[var(--ubuy-border-soft)]" />
          </div>
        </div>
      </div>
    </div>
  );
}
